import { createContext, useContext, useState, useCallback  } from 'react';

/**
 * Validates an SSE/REST diet-generation payload and returns a safe
 * subset of known fields. Returns null if the payload is not an object
 * or is missing the mandatory _id field.
 *
 * @param {unknown} raw
 * @returns {Object|null}
 */
function sanitisePayload(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const { _id, client, dietist, progress, status } = raw;
    if (typeof _id !== 'string' || !_id) return null;
    return {
        _id,
        ...(typeof client   === 'string' && { client }),
        ...(typeof dietist  === 'string' && { dietist }),
        ...(typeof progress === 'number' && { progress }),
        ...(typeof status   === 'string' && { status }),
    };
}

const DietGenerationContext = createContext(null);

/**
 * Provider component that manages diet generation state.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const DietGenerationProvider = ({ children }) => {
    const [generation, setGeneration] = useState(null);

    /**
     * Inserts or updates a diet generation entry.
     * Used for progress updates and status changes.
     *
     * @param {Object} payload
     * @param {string} payload._id - Unique generation ID
     * @param {string} payload.client - Client ID
     * @param {string} payload.dietist - Dietist ID
     * @param {number} payload.progress - Progress percentage (0–100)
     * @param {string} payload.status - Current status
     */
    const upsertGeneration = (payload) => {
        const safe = sanitisePayload(payload);
        if (!safe) return;
        setGeneration(prev => prev ? { ...prev, ...safe } : safe);
    };

    /**
     * Removes a diet generation.
     *
     * @param {string} dietGenerationId - Generation ID to remove
     */
    const removeGeneration = () => {
        setGeneration(null);
    };

    return (
        <DietGenerationContext.Provider
            value={{ generation, upsertGeneration, removeGeneration }}
        >
            {children}
        </DietGenerationContext.Provider>
    );
};

/**
 * Hook to access the DietGenerationContext.
 *
 * @returns {{
 *   generation: Object,
 *   upsertGeneration: Function,
 *   removeGeneration: Function
 * }}
 */
export const useDietGeneration = () => useContext(DietGenerationContext);
